import { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RefreshCw, Trash2, Shield, AlertCircle, Download, AlertTriangle, Check } from 'lucide-react';
import { AttendanceData, AttendanceStatus, AttendanceError } from '@/types/attendance';
import { attendanceStorage } from '@/services/attendanceStorage';
import {
  parseAttendanceHTML, parseStudentInfo, convertToAttendanceData,
  isValidAttendancePage, requiresLogin, createParseError,
} from '@/utils/attendanceParser';
import { AttendancePortalFrame } from '@/components/AttendancePortalFrame';
import { T } from './DashboardShell';

type ShellComponent = React.ComponentType<{
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}>;

const card: React.CSSProperties = {
  background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16,
};

function HeaderIconBtn({ onClick, children, label }: { onClick: () => void; children: React.ReactNode; label: string }) {
  return (
    <button onClick={onClick} aria-label={label}
      style={{
        width: 38, height: 38, borderRadius: 10, flexShrink: 0,
        background: T.surface, border: `1px solid ${T.border}`, color: T.text50,
        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
      }}>
      {children}
    </button>
  );
}

export function AttendanceContent({ Shell }: { Shell: ShellComponent }) {
  const [status, setStatus] = useState<AttendanceStatus>('idle');
  const [attendanceData, setAttendanceData] = useState<AttendanceData | null>(null);
  const [error, setError] = useState<AttendanceError | null>(null);
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [showPortalFrame, setShowPortalFrame] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [lastFetch, setLastFetch] = useState<Date | null>(null);

  useEffect(() => {
    loadStoredData();
  }, []);

  const loadStoredData = async () => {
    try {
      const consent = await attendanceStorage.getConsent();
      setConsentGiven(consent);

      if (consent) {
        const data = await attendanceStorage.getAttendance();
        if (data) {
          setAttendanceData(data);
          setLastFetch(new Date(data.fetchedAt));
          setStatus('success');
        }
      } else {
        setStatus('consent-required');
      }
    } catch (err) {
      console.error('Error loading stored data:', err);
    }
  };

  const handleConsent = async (accepted: boolean) => {
    setShowConsentDialog(false);
    if (accepted) {
      await attendanceStorage.setConsent(true);
      setConsentGiven(true);
      setStatus('idle');
    } else {
      setStatus('consent-required');
    }
  };

  const handleDataReceived = useCallback(async (data: string) => {
    setShowPortalFrame(false);
    setStatus('loading');
    setError(null);

    try {
      let parsedData: any = null;
      try {
        parsedData = JSON.parse(data);
      } catch {
        // HTML fallback
      }

      if (parsedData && parsedData.courses) {
        const courses = parsedData.courses.map((course: any, index: number) => ({
          id: `course-${index}-${Date.now()}`,
          courseName: course.courseName,
          courseCode: course.courseCode,
          totalClasses: course.totalClasses,
          presents: course.presents,
          absents: course.absents,
          attendancePercentage: course.percentage,
          lastUpdated: new Date(),
        }));

        const newAttendanceData: AttendanceData = {
          id: `attendance-${Date.now()}`,
          studentName: parsedData.studentName,
          rollNumber: parsedData.rollNumber,
          semester: parsedData.semester,
          session: parsedData.session,
          courses,
          fetchedAt: new Date(parsedData.fetchedAt || Date.now()),
        };

        await attendanceStorage.saveAttendance(newAttendanceData);
        setAttendanceData(newAttendanceData);
        setLastFetch(new Date());
        setStatus('success');
        return;
      }

      // Legacy HTML parsing
      const html = data;
      if (requiresLogin(html)) {
        setError(createParseError('login-failed'));
        setStatus('error');
        return;
      }

      if (!isValidAttendancePage(html)) {
        setError(createParseError('portal-changed'));
        setStatus('error');
        return;
      }

      const rows = parseAttendanceHTML(html);
      if (rows.length === 0) {
        setError(createParseError('parse-error', 'No attendance data found.'));
        setStatus('error');
        return;
      }

      const studentInfo = parseStudentInfo(html);
      const convertedData = convertToAttendanceData(rows, studentInfo);
      await attendanceStorage.saveAttendance(convertedData);
      setAttendanceData(convertedData);
      setLastFetch(new Date());
      setStatus('success');
    } catch (err) {
      console.error('Error processing attendance:', err);
      setError(createParseError('parse-error', String(err)));
      setStatus('error');
    }
  }, []);

  const handleClearData = async () => {
    await attendanceStorage.clearAllData();
    setAttendanceData(null);
    setLastFetch(null);
    setConsentGiven(false);
    setStatus('consent-required');
    setShowClearDialog(false);
  };

  const stats = attendanceData?.courses.length ? {
    dangerCourses: attendanceData.courses.filter(c => c.attendancePercentage < 75),
    courseCount: attendanceData.courses.length,
  } : null;

  const subtitle = lastFetch ? `Updated ${lastFetch.toLocaleDateString()}` : 'Track your attendance';

  const actions = consentGiven && !showPortalFrame ? (
    <>
      <HeaderIconBtn onClick={() => setShowPortalFrame(true)} label="Refresh attendance"><RefreshCw size={16} /></HeaderIconBtn>
      <HeaderIconBtn onClick={() => setShowClearDialog(true)} label="Clear data"><Trash2 size={16} /></HeaderIconBtn>
    </>
  ) : undefined;

  return (
    <Shell title="Attendance" subtitle={subtitle} actions={actions}>
      {/* Portal frame (inline, keeps chrome) */}
      {showPortalFrame ? (
        <AttendancePortalFrame onDataReceived={handleDataReceived} onCancel={() => setShowPortalFrame(false)} />
      ) : (
        <>
          {/* Consent required */}
          {status === 'consent-required' && (
            <div style={{ ...card, textAlign: 'center', padding: '32px 24px' }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: T.tealBg, border: `1px solid ${T.tealBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                <Shield size={26} color={T.teal} />
              </div>
              <h2 style={{ fontFamily: T.serif, fontSize: 20, fontWeight: 400, margin: '0 0 6px' }}>Privacy First</h2>
              <p style={{ fontSize: 12, color: T.text35, margin: '0 auto 18px', maxWidth: 360, fontWeight: 300, lineHeight: 1.6 }}>
                Your attendance data stays on your device unless you separately enable Owlet access. Owlet never receives your credentials.
              </p>
              <button onClick={() => setShowConsentDialog(true)}
                style={{
                  padding: '11px 22px', borderRadius: 10, background: T.tealBg, border: `1px solid ${T.tealBorder}`,
                  color: T.teal, fontFamily: T.mono, fontSize: 10, letterSpacing: '.1em', cursor: 'pointer',
                }}>GET STARTED</button>
            </div>
          )}

          {/* Loading */}
          {status === 'loading' && (
            <div style={{ ...card, textAlign: 'center', padding: '40px 24px' }}>
              <RefreshCw size={26} color={T.teal} style={{ margin: '0 auto', display: 'block', animation: 'attspin 1s linear infinite' }} />
              <p style={{ fontSize: 12, color: T.text35, marginTop: 14 }}>Loading attendance data…</p>
              <style>{`@keyframes attspin{to{transform:rotate(360deg)}}`}</style>
            </div>
          )}

          {/* Error */}
          {status === 'error' && error && (
            <div style={{ ...card, borderLeft: '3px solid #EF4444' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <AlertCircle size={18} color="#EF4444" style={{ flexShrink: 0, marginTop: 1 }} />
                <div>
                  <h3 style={{ fontSize: 13, fontWeight: 500, margin: 0 }}>{error.message}</h3>
                  {error.details && <p style={{ fontSize: 12, color: T.text35, margin: '4px 0 0', fontWeight: 300 }}>{error.details}</p>}
                  <button onClick={() => setShowPortalFrame(true)}
                    style={{
                      marginTop: 12, padding: '9px 16px', borderRadius: 9, background: T.tealBg, border: `1px solid ${T.tealBorder}`,
                      color: T.teal, fontFamily: T.mono, fontSize: 10, letterSpacing: '.08em', cursor: 'pointer',
                    }}>TRY AGAIN</button>
                </div>
              </div>
            </div>
          )}

          {/* No data */}
          {status === 'idle' && consentGiven && !attendanceData && (
            <div style={{ ...card, textAlign: 'center', padding: '40px 24px' }}>
              <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,.04)', border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                <Download size={28} color={T.text35} />
              </div>
              <h2 style={{ fontFamily: T.serif, fontSize: 20, fontWeight: 400, margin: '0 0 6px' }}>Fetch Your Attendance</h2>
              <p style={{ fontSize: 12, color: T.text35, margin: '0 0 18px', fontWeight: 300 }}>
                Connect to ETC portal to view your attendance
              </p>
              <button onClick={() => setShowPortalFrame(true)}
                style={{
                  padding: '12px 22px', borderRadius: 10, background: T.tealBg, border: `1px solid ${T.tealBorder}`,
                  color: T.teal, fontFamily: T.mono, fontSize: 10, letterSpacing: '.1em', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                }}><Download size={14} /> FETCH ATTENDANCE</button>
            </div>
          )}

          {/* Dashboard */}
          {status === 'success' && attendanceData && stats && (() => {
            const courses = attendanceData.courses;
            const totalClasses = courses.reduce((s, c) => s + c.totalClasses, 0);
            const totalPresent = courses.reduce((s, c) => s + c.presents, 0);
            const totalAbsent = courses.reduce((s, c) => s + c.absents, 0);
            const overallPct = totalClasses ? Math.round((totalPresent / totalClasses) * 100) : 0;
            const overallColor = overallPct < 75 ? '#EF4444' : overallPct >= 85 ? '#10B981' : T.teal;
            // Worst attendance first so at-risk courses surface to the top.
            const sorted = [...courses].sort((a, b) => a.attendancePercentage - b.attendancePercentage);
            return (
              <>
                {/* Overall summary hero */}
                <div style={{
                  background: 'linear-gradient(135deg, rgba(20,184,166,.12) 0%, rgba(6,182,212,.05) 100%)',
                  border: '1px solid rgba(20,184,166,.16)', borderRadius: 14, padding: '20px 22px',
                  position: 'relative', overflow: 'hidden',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap',
                }}>
                  <div style={{ position: 'absolute', top: -50, right: -30, width: 180, height: 180, background: 'radial-gradient(circle, rgba(20,184,166,.16) 0%, transparent 65%)', pointerEvents: 'none' }} />
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <span style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.16em', color: 'rgba(20,184,166,.65)' }}>OVERALL ATTENDANCE</span>
                    <div style={{ fontFamily: T.serif, fontSize: 48, fontWeight: 400, color: overallColor, lineHeight: 1, marginTop: 6 }}>{overallPct}%</div>
                    <div style={{ fontSize: 12, color: T.text50, marginTop: 6, fontWeight: 300 }}>{totalPresent} of {totalClasses} classes attended</div>
                  </div>
                  <div style={{ display: 'flex', position: 'relative', zIndex: 1 }}>
                    {[
                      { num: stats.courseCount, lbl: 'COURSES', color: T.teal },
                      { num: stats.dangerCourses.length, lbl: 'AT RISK', color: stats.dangerCourses.length ? '#EF4444' : T.teal },
                      { num: totalAbsent, lbl: 'ABSENT', color: T.teal },
                    ].map(({ num, lbl, color }, i, arr) => (
                      <div key={lbl} style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ textAlign: 'center', padding: '0 16px' }}>
                          <div style={{ fontFamily: T.serif, fontSize: 28, color, lineHeight: 1 }}>{num}</div>
                          <div style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.12em', color: T.text25, marginTop: 4 }}>{lbl}</div>
                        </div>
                        {i < arr.length - 1 && <div style={{ width: 1, height: 34, background: 'rgba(255,255,255,.08)' }} />}
                      </div>
                    ))}
                  </div>
                </div>

                {stats.dangerCourses.length > 0 && (
                  <div style={{ ...card, borderLeft: '3px solid #EF4444', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <AlertTriangle size={20} color="#EF4444" style={{ flexShrink: 0, marginTop: 1 }} />
                    <div>
                      <h3 style={{ fontSize: 13, fontWeight: 500, color: '#EF4444', margin: 0 }}>Short Attendance Warning</h3>
                      <p style={{ fontSize: 12, color: T.text50, margin: '4px 0 0', fontWeight: 300 }}>
                        {stats.dangerCourses.length} course(s) below 75% — listed first below.
                      </p>
                    </div>
                  </div>
                )}

                <div style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em', color: T.text35, textTransform: 'uppercase' }}>
                  Courses ({stats.courseCount})
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 12 }}>
                  {sorted.map(course => <CourseCard key={course.id} course={course} />)}
                </div>
              </>
            );
          })()}
        </>
      )}

      {/* Consent dialog */}
      <Dialog open={showConsentDialog} onOpenChange={setShowConsentDialog}>
        <DialogContent className="max-w-sm border-0" style={{ background: '#111', border: `1px solid ${T.borderHov}`, color: T.text }}>
          <DialogHeader>
            <DialogTitle>Privacy Notice</DialogTitle>
            <DialogDescription>How we handle your data</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '8px 0' }}>
            <div>
              <p style={{ fontSize: 13, fontWeight: 500, color: '#10B981', margin: '0 0 6px' }}>✓ What we do:</p>
              <ul style={{ fontSize: 13, color: T.text50, margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
                <li>Store data locally on your device</li>
                <li>Let you clear data anytime</li>
              </ul>
            </div>
            <div>
              <p style={{ fontSize: 13, fontWeight: 500, color: '#EF4444', margin: '0 0 6px' }}>✗ What we don't do:</p>
              <ul style={{ fontSize: 13, color: T.text50, margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
                <li>Store your password</li>
                <li>Send data without a separate Owlet opt-in</li>
              </ul>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => handleConsent(false)}>Cancel</Button>
            <Button onClick={() => handleConsent(true)}>Accept</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear dialog */}
      <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <DialogContent className="max-w-sm border-0" style={{ background: '#111', border: `1px solid ${T.borderHov}`, color: T.text }}>
          <DialogHeader>
            <DialogTitle>Clear All Data?</DialogTitle>
            <DialogDescription>
              This will delete all stored attendance data from your device.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowClearDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleClearData}>Clear Data</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

// ─── Attendance insight ────────────────────────────────────────────────────────
// At ≥75% → how many more classes you can miss and still stay ≥75%.
// Below 75% → how many classes you must attend in a row to recover to 75%.
const THRESHOLD = 0.75;
function attendanceInsight(present: number, total: number): { type: 'safe' | 'risk'; n: number } | null {
  if (!total) return null;
  if (present / total >= THRESHOLD) {
    return { type: 'safe', n: Math.max(0, Math.floor(present / THRESHOLD - total)) };
  }
  return { type: 'risk', n: Math.ceil((THRESHOLD * total - present) / (1 - THRESHOLD)) };
}

// ─── Course card ───────────────────────────────────────────────────────────────
function CourseCard({ course }: { course: AttendanceData['courses'][number] }) {
  const pct = course.attendancePercentage;
  const isDanger = pct < 75;
  const isWarning = course.absents >= 8 && pct >= 75;
  const accent = isDanger ? '#EF4444' : isWarning ? '#F59E0B' : T.teal;
  const pctColor = isDanger ? '#EF4444' : pct >= 85 ? '#10B981' : T.teal;
  const insight = attendanceInsight(course.presents, course.totalClasses);

  return (
    <div style={{ ...card, borderLeft: `3px solid ${accent}` }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h3 style={{ fontSize: 13, fontWeight: 500, margin: 0 }}>{course.courseName}</h3>
            {isDanger && <span style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.06em', color: '#EF4444', background: 'rgba(239,68,68,.12)', border: '1px solid rgba(239,68,68,.25)', borderRadius: 4, padding: '2px 6px' }}>SHORT</span>}
            {isWarning && !isDanger && <span style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.06em', color: '#F59E0B', background: 'rgba(245,158,11,.12)', border: '1px solid rgba(245,158,11,.25)', borderRadius: 4, padding: '2px 6px' }}>WARNING</span>}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontFamily: T.serif, fontSize: 30, fontWeight: 400, color: pctColor, lineHeight: 1 }}>{pct}%</div>
        </div>
      </div>

      {/* Progress bar with the 75% required marker */}
      <div style={{ position: 'relative', height: 6, borderRadius: 3, background: 'rgba(255,255,255,.06)', marginTop: 14, overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${Math.min(100, Math.max(0, pct))}%`, borderRadius: 3, background: pctColor, transition: 'width .3s' }} />
        <div style={{ position: 'absolute', left: '75%', top: 0, bottom: 0, width: 1, background: 'rgba(255,255,255,.4)' }} />
      </div>
      <div style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.08em', color: T.text25, marginTop: 4, textAlign: 'right' }}>75% REQUIRED</div>

      {/* Stat tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 12 }}>
        {[
          { lbl: 'Classes', val: course.totalClasses, color: T.text },
          { lbl: 'Present', val: course.presents, color: '#10B981' },
          { lbl: 'Absent', val: course.absents, color: '#EF4444' },
        ].map(s => (
          <div key={s.lbl} style={{ background: 'rgba(255,255,255,.02)', border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 6px', textAlign: 'center' }}>
            <div style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.06em', color: T.text35 }}>{s.lbl.toUpperCase()}</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: s.color, marginTop: 2 }}>{s.val}</div>
          </div>
        ))}
      </div>

      {/* Skip / recover insight */}
      {insight && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 7, marginTop: 12, padding: '8px 11px', borderRadius: 8,
          background: insight.type === 'safe' ? 'rgba(16,185,129,.07)' : 'rgba(239,68,68,.07)',
          border: `1px solid ${insight.type === 'safe' ? 'rgba(16,185,129,.2)' : 'rgba(239,68,68,.2)'}`,
          fontSize: 11.5, color: insight.type === 'safe' ? '#10B981' : '#EF4444',
        }}>
          {insight.type === 'safe' ? <Check size={13} style={{ flexShrink: 0 }} /> : <AlertTriangle size={13} style={{ flexShrink: 0 }} />}
          {insight.type === 'safe'
            ? (insight.n > 0 ? `You can skip ${insight.n} more class${insight.n > 1 ? 'es' : ''} and stay above 75%` : 'On the edge — don’t miss any more')
            : `Attend ${insight.n} class${insight.n > 1 ? 'es' : ''} in a row to recover to 75%`}
        </div>
      )}
    </div>
  );
}
